/* eslint-disable react/no-unescaped-entities */
import { FruitsCard } from "../components/FruitsCard";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../contexts/AppContext";
import { api } from "../lib/api";
import { speak } from "../lib/i18n";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { ArrowLeft, Activity, Pill, HeartPulse, Smile, Check, X, Minus, Volume2, Share2, Image as ImageIcon, Scale, Droplets, Award, Flame, Loader2, Utensils } from "lucide-react";

const ROWS = [
  { key: "exercice", label: "Exercices", ln: "Ngalasisi", Icon: Activity, color: "#2BA3A0" },
  { key: "medicaments", label: "Médicaments", ln: "Nkisi", Icon: Pill, color: "#3a8acb" },
  { key: "tension", label: "Tension", ln: "Tansio", Icon: HeartPulse, color: "#D9534F" },
  { key: "humeur", label: "Humeur", ln: "Motema", Icon: Smile, color: "#D4AF5A" },
];
const MOODS = ["", "😞", "😕", "😐", "🙂", "😄"];

function Cell({ value, row }) {
  if (row.key === "humeur") return <span className="text-xl leading-none">{value ? MOODS[value] : <Minus size={16} className="text-[#D8D3C4] mx-auto" />}</span>;
  if (value === null || value === undefined) return <Minus size={16} className="text-[#D8D3C4] mx-auto" />;
  return value
    ? <span className="w-8 h-8 rounded-full grid place-items-center text-white mx-auto" style={{ backgroundColor: row.color }}><Check size={18} strokeWidth={3} /></span>
    : <span className="w-8 h-8 rounded-full grid place-items-center text-[#D8D3C4] border-2 border-[#E4E0D5] mx-auto"><X size={16} /></span>;
}

export default function FamilyBoardPage() {
  const navigate = useNavigate();
  const { patient, lang, online } = useApp();
  const [data, setData] = useState(() => { try { return JSON.parse(localStorage.getItem("bc_family_week") || "null"); } catch { return null; } });
  const [sharing, setSharing] = useState(false);
  const ln = lang === "ln";

  useEffect(() => {
    if (!patient) return;
    api.get(`/patients/${patient.id}/family-week`).then(({ data }) => { setData(data); localStorage.setItem("bc_family_week", JSON.stringify(data)); }).catch(() => null);
  }, [patient, online]);

  if (!patient) return <div className="pt-10 text-center text-[#0A1426]/70">Aucun patient lié.</div>;
  if (!data) return <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#2BA3A0]" /></div>;

  const t = data.totaux;
  const encouragement = ln ? data.encouragement_ln : data.encouragement_fr;
  const prenom = data.patient_nom.split(" ")[0];
  const summaryText = ln
    ? `${prenom} : pɔsɔ oyo, biteyelo ${t.exercices} na 7, tansio emekami mikolo ${t.tensions}, badges ${data.badges.length}. ${encouragement}`
    : `Cette semaine, ${prenom} a fait ${t.exercices} séance${t.exercices > 1 ? "s" : ""} sur 7, mesuré sa tension ${t.tensions} jour${t.tensions > 1 ? "s" : ""}${t.humeur_moyenne ? `, humeur moyenne ${t.humeur_moyenne} sur 5` : ""}, et a ${data.badges.length} badge${data.badges.length > 1 ? "s" : ""}. ${encouragement}`;

  const shareText = () => {
    const txt = `🌿 Bombacare — La semaine de ${data.patient_nom}\n✅ Exercices : ${t.exercices}/7\n💊 Médicaments complets : ${t.medicaments_ok}/7\n❤️ Tension mesurée : ${t.tensions}/7${t.humeur_moyenne ? `\n🙂 Humeur moyenne : ${t.humeur_moyenne}/5` : ""}${data.poids ? `\n⚖️ Poids : ${data.poids.valeur} kg` : ""}${data.glycemie ? `\n🩸 Glycémie : ${data.glycemie.valeur} ${data.glycemie.unite}` : ""}${data.repas && data.repas.score != null ? `\n🍽️ Repas de la semaine : ${data.repas.score}/10 (${data.repas.photos} photo(s))${data.repas.objectif?.atteint ? " — objectif atteint !" : ""}` : ""}\n🏅 Badges : ${data.badges.length} · 🔥 ${data.serie_jours} jours de suite\n\n${encouragement}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(txt)}`, "_blank", "noopener");
  };

  const sharePng = async () => {
    setSharing(true);
    try {
      const { data: blob } = await api.get(`/patients/${patient.id}/family-week.png?ln=${ln ? 1 : 0}`, { responseType: "blob" });
      const file = new File([blob], "bombacare-semaine.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `La semaine de ${data.patient_nom}`, text: encouragement });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = file.name; a.click();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
        toast.success("Image téléchargée — partagez-la sur WhatsApp");
      }
    } catch (e) { if (e?.name !== "AbortError") toast.error("Partage impossible pour le moment"); }
    setSharing(false);
  };

  return (
    <div className="bc-in pt-2 pb-6 flex flex-col gap-4" data-testid="family-board">
      <FruitsCard />
      <div className="flex items-center gap-3">
        <button data-testid="family-back" onClick={() => navigate("/app")} className="w-11 h-11 grid place-items-center rounded-full bg-white border border-[#E4E0D5] text-[#0A1426]"><ArrowLeft size={20} /></button>
        <div className="flex-1">
          <h1 className="font-[Fraunces] text-2xl font-bold text-[#0A1426] leading-tight">{ln ? "Pɔsɔ ya" : "La semaine de"} {prenom}</h1>
          <div className="text-[12px] text-[#0A1426]/60">{new Date(data.semaine_debut).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })} → {new Date(data.semaine_fin).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })} · {ln ? "Tableau ya libota" : "Tableau famille"}</div>
        </div>
        <button data-testid="family-speak-btn" onClick={() => speak(summaryText, lang)} className="w-11 h-11 rounded-full bg-[#0A1426] text-white grid place-items-center"><Volume2 size={20} /></button>
      </div>

      <section className="bg-white rounded-3xl border border-[#E4E0D5] p-4 overflow-hidden" data-testid="family-week-grid">
        <div className="grid" style={{ gridTemplateColumns: "92px repeat(7, 1fr)" }}>
          <div />
          {data.jours.map((d) => (
            <div key={d.date} className={`text-center text-[11px] font-bold pb-2 ${d.date === data.semaine_fin ? "text-[#2BA3A0]" : "text-[#0A1426]/60"}`}>{d.jour}</div>
          ))}
          {ROWS.map((row) => (
            <div key={row.key} className="contents">
              <div className="flex items-center gap-1.5 py-2.5 text-[12px] font-bold text-[#0A1426]"><row.Icon size={16} style={{ color: row.color }} /><span className="truncate">{ln ? row.ln : row.label}</span></div>
              {data.jours.map((d) => <div key={d.date + row.key} data-testid={`cell-${row.key}-${d.date}`} className="flex items-center justify-center py-2.5"><Cell value={d[row.key]} row={row} /></div>)}
            </div>
          ))}
        </div>
      </section>

      <div className="grid grid-cols-4 gap-2">
        {[[t.exercices + "/7", ln ? "biteyelo" : "séances", Activity, "#2BA3A0"], [t.tensions + "/7", ln ? "tansio" : "tensions", HeartPulse, "#D9534F"],
          [data.badges.length, "badges", Award, "#D4AF5A"], [data.serie_jours, ln ? "mikolo" : "jours de suite", Flame, "#D9534F"]].map(([v, l, I, c]) => (
          <div key={l} className="bg-white rounded-2xl border border-[#E4E0D5] p-3 text-center">
            <I size={18} className="mx-auto" style={{ color: c }} />
            <div data-testid={`family-stat-${l.replace(/\s/g, "-")}`} className="font-[Fraunces] text-2xl font-bold text-[#0A1426] leading-tight mt-1">{v}</div>
            <div className="text-[10px] text-[#0A1426]/60 leading-tight">{l}</div>
          </div>
        ))}
      </div>

      {(data.poids || data.glycemie) && (
        <div className="grid grid-cols-2 gap-2">
          {data.poids && <div data-testid="family-poids" className="bg-white rounded-2xl border border-[#E4E0D5] p-3 flex items-center gap-3"><Scale size={22} className="text-[#2BA3A0]" /><div><div className="font-bold text-[#0A1426] text-lg leading-tight">{data.poids.valeur} kg</div><div className="text-[10px] text-[#0A1426]/60">{ln ? "Kilo" : "Dernier poids"}</div></div></div>}
          {data.glycemie && <div data-testid="family-glycemie" className="bg-white rounded-2xl border border-[#E4E0D5] p-3 flex items-center gap-3"><Droplets size={22} className="text-[#D9534F]" /><div><div className="font-bold text-[#0A1426] text-lg leading-tight">{data.glycemie.valeur} <span className="text-[11px]">{data.glycemie.unite}</span></div><div className="text-[10px] text-[#0A1426]/60">{ln ? "Glycémie" : "Dernière glycémie"}</div></div></div>}
        </div>
      )}

      {data.repas && (
        <section data-testid="family-repas" className="bg-white rounded-3xl border border-[#E4E0D5] p-4 flex items-center gap-4">
          <div className="w-16 h-16 rounded-full grid place-items-center shrink-0 border-4" style={{ borderColor: data.repas.score == null ? "#E4E0D5" : data.repas.score >= 7 ? "#5CB85C" : data.repas.score >= 4 ? "#D4AF5A" : "#D9534F" }}>
            <div className="text-center leading-none"><div className="font-[Fraunces] text-xl font-bold text-[#0A1426]">{data.repas.score ?? "—"}</div><div className="text-[9px] text-[#0A1426]/60">/10</div></div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-[#0A1426] flex items-center gap-2"><Utensils size={16} className="text-[#D4AF5A]" /> {ln ? "Bilei ya pɔsɔ" : "Repas de la semaine"}</div>
            <div className="text-[12px] text-[#0A1426]/80">{data.repas.photos} photo(s){data.repas.glycemie_moyenne ? ` · glycémie moy. ${data.repas.glycemie_moyenne} mg/dL` : ""}</div>
            {data.repas.objectif && <div className="text-[11px] font-bold mt-0.5" style={{ color: data.repas.objectif.atteint ? "#3d8b3d" : "#8A6D2B" }}>{ln ? "Mokano ya monganga" : "Objectif du médecin"} ≥ {data.repas.objectif.score_cible}/10 — {data.repas.objectif.atteint ? (ln ? "ekokisami !" : "atteint !") : `${data.repas.objectif.progression} %`}</div>}
          </div>
        </section>
      )}

      {data.badges.length > 0 && (
        <section className="bg-white rounded-3xl border border-[#E4E0D5] p-4" data-testid="family-badges">
          <div className="text-[11px] uppercase tracking-wider text-[#0A1426]/60 font-bold mb-2">{ln ? "Badges" : "Badges gagnés"}</div>
          <div className="flex flex-wrap gap-2">
            {data.badges.map((b) => (
              <span key={b.code} className={`px-3 py-1.5 rounded-full text-[12px] font-bold ${data.badges_semaine.includes(b.code) ? "ring-2 ring-[#D4AF5A]" : ""}`} style={{ backgroundColor: `${b.couleur}1f`, color: b.couleur }}>{b.titre}</span>
            ))}
          </div>
        </section>
      )}

      <section data-testid="family-encouragement" className="rounded-3xl p-5 bg-[#0A1426] text-white">
        <p className="font-[Fraunces] text-lg font-bold leading-snug">« {encouragement} »</p>
        <div className="grid grid-cols-2 gap-2 mt-4">
          <Button data-testid="family-share-whatsapp" onClick={shareText} className="h-12 rounded-full bg-[#25D366] hover:bg-[#1da851] text-white font-bold"><Share2 size={16} className="mr-2" /> WhatsApp</Button>
          <Button data-testid="family-share-png" onClick={sharePng} disabled={sharing} className="h-12 rounded-full bg-[#D4AF5A] hover:bg-white text-[#0A1426] font-bold">{sharing ? <Loader2 size={16} className="animate-spin" /> : <><ImageIcon size={16} className="mr-2" /> Image</>}</Button>
        </div>
      </section>
    </div>
  );
}
